// Shared constants, design tokens, and prompt templates for Seedance VFX

export const DARK = {
  bg: "#08080C", surface: "#111118", card: "#16161F", border: "#2A2A38",
  text: "#E8E8F0", textMuted: "#8080A0", textDim: "#4A4A62",
  accent: "#E8930A", accentDim: "#3D2700", accentText: "#FFBA4A",
  green: "#22C55E", greenDim: "#052010", red: "#EF4444", redDim: "#250808",
  blue: "#60A5FA",
};

export const RATIOS = ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"];
// Seedance 2.0 duration window: tested down to 2s in production (the
// hard floor was previously believed to be 4 per the docs but Adrian
// confirmed 2-3s shots work end-to-end; ARK no longer silently coerces).
// Upper bound stays at 15 — the native model can't generate more than
// that per call. Sequence chaining covers longer shots.
export const MIN_DURATION = 2;
export const MAX_DURATION = 15;
export const DEFAULT_DURATION = 5;
// ARK dreamina-seedance-2-0 requires the SOURCE reference video to be at
// least 1.8 seconds long. Shorter clips are rejected by the model. This is
// independent of our output `duration` parameter.
export const MIN_SOURCE_DURATION = 1.8;
export const MODEL_ID = "dreamina-seedance-2-0-260128";
// Seedance 2.5 y su regla de selección viven en src/lib/modelSelect.js: son
// lógica pura y así entran en el gate de CI (`*.test.mjs`).

/**
 * Generic "clean the plate" clause appended to every template's prompt.
 * Production plates frequently carry on-screen overlays that should never
 * survive into a delivered shot (supervisor slates, timecode burn-ins,
 * sketch annotations, watermarks, frame numbers). The clause is written as
 * a SAFEGUARD — if the plate is already clean, the model should leave it
 * alone, so adding this clause never produces hallucinated overlays.
 */
export const PRODUCTION_OVERLAY_CLEANUP =
  "Cleanup pass (safeguard, mandatory from frame 1): if the source plate carries any on-screen production overlays that are NOT part of the photographic content — slate text and shot codes (e.g. 'BT_018_0025', 'ADD ORCA'), red supervisor annotations, timecode burn-ins (HH:MM:SS:FF), frame numbers, watermarks, hand-drawn ink / pencil sketch lines, pose-guide strokes, arrows, framing brackets, or letterbox bars carrying such overlays — remove them entirely from every frame. Reconstruct the area beneath each removed overlay so it matches the surrounding plate with no clone-stamp seams, no ghost text, no residual ink, and no blur halos. If the plate is already clean, leave it untouched — do NOT invent overlays.";

/**
 * Common "Avoid" suffix listing the failure modes for the cleanup pass.
 */
export const PRODUCTION_OVERLAY_AVOID =
  "residual sketch lines or pencil strokes, leftover slate text or timecode burn-ins, ghosting of removed annotations, clone-stamp patterns in the reconstructed sky / water / background";

/**
 * Format a structured continuity prompt with headed sections.
 * Returns a single string with section headers. Empty / missing values are
 * skipped so the operator never sees empty headers.
 *
 * Section order matches the production continuity stack:
 *   SOURCE LOCK / ALLOWED CHANGE / SUBJECT / ACTION / ENVIRONMENT /
 *   CAMERA / LIGHTING/MOOD / STYLE / CONSTRAINTS
 *
 * Every prompt also receives the same CONSTRAINTS suffix appended at the
 * end (production-overlay cleanup) so the model never has to guess about it.
 */
export function formatStructuredPrompt(s) {
  const out = [];
  function add(header, body) {
    if (!body) return;
    const t = String(body).trim();
    if (!t) return;
    out.push(header + ":\n" + t);
  }
  add("SOURCE LOCK",
      s.sourceLock ||
      "Use @Video1 as the absolute source plate. Preserve timing, camera movement, framing, lens perspective, lighting, geography, subject identity and motion.");
  add("ALLOWED CHANGE", s.allowedChange);
  add("SUBJECT", s.subject);
  add("ACTION", s.action);
  add("ENVIRONMENT", s.environment);
  add("CAMERA", s.camera || "Preserve source camera exactly. Do not change framing or motion.");
  add("LIGHTING/MOOD", s.lighting);
  add("STYLE", s.style || "Photorealistic production footage. No stylization.");
  // CONSTRAINTS — start with the template-specific baseline, then append the
  // generic production-overlay cleanup safeguard so every shot inherits it.
  const constraints = [
    s.constraints,
    "Do not change camera. Do not redesign scene. Preserve proportions. Keep motion steady. No extra objects. No identity changes.",
    PRODUCTION_OVERLAY_CLEANUP,
  ].filter(Boolean).map((x) => x.trim()).join(" ");
  add("CONSTRAINTS", constraints);
  // AVOID — keep the template-specific avoid list + global overlay avoids.
  const avoid = [s.avoid, PRODUCTION_OVERLAY_AVOID].filter(Boolean).join(", ");
  add("AVOID", avoid);
  return out.join("\n\n");
}

export const TEMPLATES = {
  background: {
    id: "background", label: "Background Replace", icon: "🌆",
    tagline: "Swap environment, keep performance",
    color: "#7C3AED", colorDim: "#1A0A35", hasImageRef: true,
    imageRefLabel: "Background reference image",
    imageRefHint: "Optional — photo of the desired background. The model will match its environment, lighting mood, color palette, and depth.",
    fields: [
      { key: "env", label: "New environment description", type: "textarea", placeholder: "e.g. neon-lit Tokyo street at night, rain-slicked asphalt, orange street lamps" },
      { key: "timeOfDay", label: "Time of day", type: "select", options: ["Retain original", "Golden hour", "Blue hour", "Midday sun", "Night", "Overcast day", "Dawn"] },
      { key: "lightMatch", label: "Light matching", type: "select", options: ["Auto (recommended)", "Hard directional", "Soft diffuse", "Practical-motivated", "Mixed natural+practical"] },
      { key: "depthCues", label: "Depth / atmosphere", type: "text", placeholder: "e.g. light fog, distant bokeh lights, volumetric shafts" },
    ],
    buildPrompt: function(f, hasImg) {
      const env = f.env || "[describe new background environment]";
      const timeOfDay = (f.timeOfDay && f.timeOfDay !== "Retain original") ? f.timeOfDay : null;
      const lightMatch = (f.lightMatch && f.lightMatch !== "Auto (recommended)") ? f.lightMatch : null;
      const imgRef = hasImg ? " Use @Image1 as the visual reference for this new environment — match its lighting mood, color palette, and depth." : "";
      return formatStructuredPrompt({
        allowedChange: "Replace the background environment ONLY. Foreground actors, their lighting and the camera stay locked.",
        subject: "Same actors as in @Video1 — wardrobe, skin tone, gestures, expressions, micro-movements, scale and proxemics all unchanged.",
        action: "Actors continue their original performance unchanged.",
        environment: env + "." + imgRef + (timeOfDay ? " Time of day: " + timeOfDay + "." : "") + (f.depthCues ? " Depth cues: " + f.depthCues + "." : ""),
        camera: "Preserve original framing, lens FOV, depth of field and any camera motion exactly.",
        lighting: (lightMatch ? lightMatch + " lighting that matches the new environment." : "Foreground lighting on the actors matches the new environment from frame 1 — key direction, fill ratio, specular highlights and eye catchlights all consistent."),
        constraints: "Apply the new background from frame 1 — no fade-in, no transition; the new environment is fully in place from the very first frame and remains stable across every frame.",
        avoid: "edge ghosting on foreground subjects, foreground flicker, compositing seams, hair/wisp keying artifacts, ground plane discontinuity, per-frame lighting drift, lighting that ramps in over time instead of being locked from frame 1",
      });
    },
  },
  remove: {
    id: "remove", label: "Object Removal", icon: "🪄",
    tagline: "Clean plate inpainting, zero artifacts",
    color: "#059669", colorDim: "#021A0E", hasImageRef: true,
    imageRefLabel: "Reference / mask hint image",
    imageRefHint: "Optional — paint the area to remove in solid color, or sketch how the clean plate should look. The model uses it as a guide for where to inpaint.",
    fields: [
      { key: "object", label: "Object(s) to remove", type: "textarea", placeholder: "e.g. boom microphone top-right corner, cable on floor, reflector stand left edge" },
      { key: "fillStrategy", label: "Fill strategy", type: "select", options: ["Background reconstruction (auto)", "Static hold from adjacent frames", "Synthesize matching texture", "Reveal occluded background"] },
      { key: "motionComp", label: "Motion compensation", type: "select", options: ["Full optical flow tracking", "Planar track", "Static background only"] },
      { key: "extras", label: "Additional notes", type: "text", placeholder: "e.g. background behind the boom is a brick wall" },
    ],
    buildPrompt: function(f, hasImg) {
      const obj = f.object || "[describe object(s) to remove]";
      const fill = (f.fillStrategy && f.fillStrategy !== "Background reconstruction (auto)") ? f.fillStrategy : null;
      const motion = (f.motionComp && f.motionComp !== "Full optical flow tracking") ? f.motionComp : null;
      const imgRef = hasImg ? " Use @Image1 as a mask/clean-plate hint — the indicated region is what must be removed and the rest of @Image1 shows the desired reconstructed background." : "";
      return formatStructuredPrompt({
        allowedChange: "Remove the indicated object(s) and seamlessly reconstruct the background behind them. Foreground actors and camera stay locked.",
        subject: "All actors and remaining objects in @Video1 — bodies, gestures, expressions, movement, wardrobe — unchanged. Foreground untouched.",
        action: obj + imgRef + (f.extras ? " Context: " + f.extras + "." : ""),
        environment: "Reconstruct background behind the removed region matching surrounding geometry, texture and lighting." + (fill ? " Fill strategy: " + fill + "." : "") + (motion ? " Motion: " + motion + "." : ""),
        camera: "Camera and framing unchanged.",
        lighting: "Preserve original lighting on the entire frame (key direction, fill ratio, ambient color, specular highlights, eye catchlights, cast shadow directions on remaining objects). The inpainted region must match this lighting exactly from frame 1.",
        style: "Invisible edit, clean-plate quality. No stylization.",
        constraints: "Apply the removal from frame 1 — no fade-in, no transition; the object is fully gone from the very first frame and the inpaint stays consistent across every frame.",
        avoid: "smearing, ghosting, flickering in the repaired region, texture stretching, visible seams between inpainted and original areas, shadows from the removed object, lighting mismatch between inpainted region and the rest of the plate, per-frame lighting drift, gradual reveal of the removal",
      });
    },
  },
  // LT — LOCATION TRANSPORT referencia-primero. Mismo patrón que Relight: el
  // grid de la localización (asset role=location, multi-vista = el MISMO
  // lugar) ES la instrucción; el texto modula. La decisión clave — qué luz
  // manda — es un TOGGLE de 3 modos VISIBLE junto al grid (no en avanzado):
  // keep (default, conserva continuidad y modeling del acting) / adopt /
  // blend (dirección original, atmósfera nueva — el que más se usa). El
  // camera/ground match desaparece como opción: "match original exactly" es
  // SIEMPRE la regla (está en la plantilla) — cambiar la cámara del plate no
  // es transporte, es otro plano. Espejo de backend/location_transport.py.
  location: {
    id: "location", label: "Location Transport", icon: "📍",
    tagline: "Referencia-primero · el grid es la localización",
    color: "#2563EB", colorDim: "#050D25", hasImageRef: true,
    imageRefLabel: "Location grid (la nueva localización)",
    imageRefHint: "Sube o elige el grid de la localización (multi-vista tipo hoja de referencia, o foto única; reutilizable: role location en la librería). El transport completo se compila desde él.",
    fields: [
      { key: "lightMode", label: "Iluminación tras el transporte", type: "select", options: [
        "Mantener luz original (default)",
        "Adoptar luz de la localización",
        "Mezcla — dirección original, atmósfera nueva",
      ] },
      { key: "location", label: "Modulación de la localización", type: "textarea", advanced: true, placeholder: "con grid: modula (p. ej. 'market stalls in the background') · sin grid: describe la localización objetivo" },
      { key: "colorGrade", label: "Color grading approach", type: "select", advanced: true, options: ["Photorealistic (no grade)", "Match location reference", "Warm / Mediterranean", "Cool / Northern", "Desaturated / documentary", "High contrast / cinematic"] },
      { key: "props", label: "Environmental props / dressing", type: "text", advanced: true, placeholder: "e.g. market stalls in background, passersby, parked mopeds" },
    ],
    buildPrompt: function(f, hasImg) {
      // LT2 — bloques por modo de luz (mismo texto que location_transport.py).
      const LIGHT_BLOCKS = {
        keep: "Keep the original lighting of @Video1 completely unchanged — direction, color temperature, contrast and mood exactly as filmed. The new location adapts to this light (its surfaces and atmosphere lit consistently by it), never the other way.",
        adopt: "Adopt the lighting of the location in @Image1: its light direction, color temperature, atmosphere and mood govern the whole shot — relight the subjects and the boat coherently with the new environment, physically plausible, shadows consistent.",
        blend: "Keep the original light DIRECTION on the subjects (their shadows and modeling as filmed), but adopt the location's color temperature, atmospheric quality and mood from @Image1 — the subjects sit naturally in the new environment without their key light changing direction.",
      };
      const mode = (f.lightMode || "").indexOf("Adoptar") === 0 ? "adopt"
        : (f.lightMode || "").indexOf("Mezcla") === 0 ? "blend" : "keep";
      if (hasImg) {
        // LT1 — plantilla canónica.
        let base = "Relocate @Video1 to the location shown in @Image1: replace the environment — terrain, background, architecture, surface materials and atmospheric feel — with this location, matched to the original camera, perspective and ground plane exactly.\n"
          + "@Image1 is a location reference ONLY — do not copy its people, vehicles or incidental objects; multiple views in the reference show the SAME place.\n"
          + "The subjects and their action remain exactly as filmed, frame for frame: the man, the boat, their movement and timing, framing, focal length, grain — untouched. Only the environment changes, consistently across the entire clip, every frame.\n"
          + LIGHT_BLOCKS[mode];
        // LT3 — lo manual MODULA ("As @Image1 but with…"), nunca contradice.
        const mods = [];
        if (f.location && f.location.trim()) mods.push(f.location.trim());
        if (f.props && f.props.trim()) mods.push(f.props.trim());
        if (f.colorGrade && f.colorGrade !== "Photorealistic (no grade)" && f.colorGrade !== "Match location reference") {
          mods.push("a " + f.colorGrade.toLowerCase() + " grade");
        }
        if (mods.length) {
          base += "\nAs @Image1 but with " + mods.join("; ") + " — this refines the location reference, it does not replace it.";
        }
        return base;
      }
      // Sin grid — modo manual (descripción pura, misma disciplina de cámara).
      const loc = (f.location || "[describe target location]").trim();
      return "Relocate @Video1 to: " + loc + ". Replace the environment — terrain, background, architecture, surface materials and atmospheric feel — matched to the original camera, perspective and ground plane exactly."
        + (f.props && f.props.trim() ? " Background dressing: " + f.props.trim() + "." : "")
        + "\nThe subjects and their action remain exactly as filmed, frame for frame — untouched. Only the environment changes, consistently across the entire clip, every frame.\n"
        + LIGHT_BLOCKS[mode];
    },
  },
  // LR — RELIGHT referencia-primero. Principio del módulo: la imagen ES la
  // instrucción de luz; el texto solo modula o arbitra. Camino feliz = UNA
  // acción (subir/elegir la lighting reference) → plantilla canónica LR1
  // (espejo del default versionado en backend/relight.py; la Guide puede
  // sobreescribirla para el autopilot). El formulario antiguo pasa COMPLETO a
  // "Ajustes avanzados" (advanced: true): con ref presente MODULA ("Match
  // @Image1 but with…"), nunca contradice; sin ref, modo manual puro (como hoy).
  lighting: {
    id: "lighting", label: "Relight", icon: "💡",
    tagline: "Referencia-primero · la imagen es la instrucción de luz",
    color: "#D97706", colorDim: "#2A1600", hasImageRef: true,
    imageRefLabel: "Lighting reference (la instrucción de luz)",
    imageRefHint: "Sube o elige la referencia de luz — un frame de película, lighting test o take aprobado (reutilizable: role lighting en la librería). El relight completo se compila desde ella; los ajustes avanzados solo la modulan.",
    fields: [
      { key: "lightSetup", label: "Modulación / descripción de luz", type: "textarea", advanced: true, placeholder: "con ref: modula (p. ej. 'harder shadows, slightly warmer') · sin ref: describe la luz objetivo completa" },
      { key: "lightType", label: "Light source type", type: "select", advanced: true, options: ["Hard directional (sun / HMI)", "Soft box / diffused", "Practical motivated (window, lamp)", "Neon / colored practicals", "Moonlight / night ext.", "Firelight / flicker", "Mixed natural + practical"] },
      { key: "colorTemp", label: "Color temperature", type: "select", advanced: true, options: ["2700K – candlelight", "3200K – tungsten", "4300K – warm fluorescent", "5600K – daylight neutral", "6500K – overcast / shade", "7000K+ – blue sky", "Mixed / split"] },
      { key: "shadowHardness", label: "Shadow quality", type: "select", advanced: true, options: ["Hard", "Semi-soft", "Soft / wrap", "No visible shadows"] },
    ],
    buildPrompt: function(f, hasImg) {
      if (hasImg) {
        // LR1 — plantilla canónica, mismo texto que backend/relight.py.
        let base = "Relight @Video1 to match the lighting of @Image1: adopt its light direction, quality (hardness/softness of shadows), color temperature, contrast ratio and overall mood. Apply this lighting consistently across the entire clip, every frame, coherent with the camera and geometry of the original scene.\n"
          + "@Image1 is a lighting reference ONLY — do not copy its scene, subjects, composition or content. Only its light.\n"
          + "Everything else in @Video1 stays exactly as the original, frame for frame: the subjects and their movement, framing, focal length, grain. Only the lighting changes.\n"
          + "Physically plausible result — shadows consistent with the new light direction, no flicker, no artifacts.";
        // LR2 — REGLA DURA: lo manual MODULA como refinamiento relativo
        // ("Match @Image1 but with…"), nunca una instrucción de luz paralela.
        const mods = [];
        if (f.lightSetup && f.lightSetup.trim()) mods.push(f.lightSetup.trim());
        if (f.lightType) mods.push("a " + f.lightType.toLowerCase() + " source quality");
        if (f.colorTemp) mods.push("color temperature leaning " + f.colorTemp);
        if (f.shadowHardness) mods.push(f.shadowHardness.toLowerCase() + " shadows");
        if (mods.length) {
          base += "\nMatch @Image1 but with " + mods.join("; ") + " — this refines the reference, it does not replace it.";
        }
        return base;
      }
      // Sin ref — el modo avanzado funciona como hoy: descripción manual pura.
      const setup = (f.lightSetup || "[describe target lighting]").trim();
      const extra = [];
      if (f.lightType) extra.push("Source type: " + f.lightType + ".");
      if (f.colorTemp) extra.push("Color temperature: " + f.colorTemp + ".");
      if (f.shadowHardness) extra.push("Shadows: " + f.shadowHardness + ".");
      return "Relight @Video1: " + setup + "." + (extra.length ? " " + extra.join(" ") : "")
        + " Everything else stays exactly as the original, frame for frame: the subjects and their movement, framing, focal length, grain. Only the lighting changes."
        + " Physically plausible result — shadows consistent with the new light direction, no flicker, no artifacts."
        + " Apply the new lighting from frame 1 — no fade-in, no transition.";
    },
  },
  custom: {
    id: "custom", label: "Custom Video Edit", icon: "✂",
    tagline: "Free-form edit · clip + reference frame + prompt",
    color: "#A78BFA", colorDim: "#160A2F",
    hasImageRef: true,
    isCustomFreeform: true,
    imageRefLabel: "Reference frame (optional)",
    imageRefHint: "Optional — a still you want the model to mimic (composition, lighting, palette, prop look). Attached as @Image1.",
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea",
        placeholder: "Describe exactly the edit you want on the clip. e.g. 'Add a sunset orange grade and a thin layer of haze drifting through the frame. Keep the actors and camera motion intact.'" },
    ],
    buildPrompt: function(f, _hasImg) {
      const p = (f.prompt || "").trim();
      return p || "[describe the edit you want on the clip]";
    },
  },
  image_to_video: {
    id: "image_to_video", label: "Image to Video", icon: "🖼",
    tagline: "Animate a still · first frame + optional last frame + prompt",
    color: "#F472B6", colorDim: "#2A0820",
    hasImageRef: false,
    isImageToVideo: true,
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea",
        placeholder: "Describe the motion / camera / action you want. e.g. 'Slow dolly in on the subject as the wind moves the curtains. Subtle parallax on the background.'" },
    ],
    buildPrompt: function(f, _hasImg) {
      const p = (f.prompt || "").trim();
      return p || "[describe the motion / camera / action]";
    },
  },
  // Omni-Reference: Seedance 2.0 multimodal-reference-to-video.
  // Source clip ([Video1]) + up to 9 reference images ([Image1]…[Image9]).
  // Limits / placeholder syntax verified against BytePlus ARK docs:
  //   https://docs.byteplus.com/en/docs/ModelArk/2298881
  omni_reference: {
    id: "omni_reference", label: "Omni-Reference", icon: "🧬",
    tagline: "Source clip + up to 9 labelled refs · multimodal-reference-to-video",
    color: "#06B6D4", colorDim: "#062535",
    hasImageRef: false,
    isOmniReference: true,
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea",
        placeholder: "Describe the edit. Type @ to insert a reference once you've added a source clip or images — e.g. 'Replace the cable in @video1 using the rope texture from @image1.'" },
    ],
    buildPrompt: function(f, _hasImg) {
      const p = (f.prompt || "").trim();
      return p || "[describe the edit; reference your images as @image1, @image2, … and the source clip as @video1]";
    },
  },
};

// Shared style helpers
export const styles = {
  input: {
    background: DARK.surface, border: "1px solid " + DARK.border, borderRadius: 6,
    color: DARK.text, fontSize: 13, padding: "8px 12px", outline: "none", fontFamily: "inherit",
  },
  btnPrimary: {
    background: DARK.accent, color: "#000", border: "1px solid " + DARK.accent,
    borderRadius: 7, padding: "10px 20px", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
  },
  btnSecondary: {
    background: "transparent", color: DARK.text, border: "1px solid " + DARK.border,
    borderRadius: 7, padding: "10px 20px", fontSize: 13, cursor: "pointer", fontFamily: "inherit",
  },
  card: {
    background: DARK.card, border: "1px solid " + DARK.border, borderRadius: 10, padding: "16px 20px",
  },
  cardTitle: {
    fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase",
    color: DARK.textMuted, marginBottom: 14, display: "flex", alignItems: "center", gap: 8,
  },
  col: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, color: DARK.textMuted },
};
